import { useAppSelector } from 'app/hooks';
import { Button } from 'components/ui/button';
import { useDebounce } from 'hooks';
import { NOTIFICATION } from 'modules/alert/constants';
import { showSuccessMessage, showUncaughtErrorMessage } from 'modules/alert/utils';
import { NewThreadButton } from 'modules/header/components/NewThreadButton';
import { useModal } from 'modules/modals/ModalProvider';
import * as Layout from 'modules/relatedThreads/components/Layout';
import RelatedThread from 'modules/relatedThreads/components/RelatedThread';
import {
  useCreateRelatedThreadMutation,
  useSearchRelatedThreadsQuery,
} from 'modules/relatedThreads/relatedThreadsApi';
import { ICreateThreadMutation } from 'modules/relatedThreads/types';
import { selectThread } from 'modules/threads/threadsSlice';
import { useGetUserInfoQuery } from 'modules/user/userApi';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import colors from 'theme/colors';
import { Divider, Group, Icon, Stack, TextInput, Title } from 'ui';
import { getThreadIdFromUrl, isValidUrl } from 'utils/helpers';

interface IProps {
  threadId: string;
}

interface FormState {
  threadIds: string[];
}

const PAGINATE_BY = 25;

const AddRelatedThread = ({ threadId }: IProps) => {
  const [search, setSearch] = useState<string>('');
  const [relatedThreadId, setRelatedThreadId] = useState<string>('');
  const [pagination, setPagination] = useState<number>(PAGINATE_BY);
  const { close, open } = useModal();
  const { data: user } = useGetUserInfoQuery();
  const { name, tags } = useAppSelector(selectThread(threadId));

  const selectedThreadsListRef = useRef<HTMLDivElement>(null);
  const [showStickySelectedThreadsInfo, setShowStickySelectedThreadsInfo] = useState(false);

  const deferredSearch = useDebounce(search, 500);

  const [createRelatedThread, { isLoading }] = useCreateRelatedThreadMutation();
  const { data } = useSearchRelatedThreadsQuery({
    id: threadId,
    name: deferredSearch,
    relatedThreadId,
    limit: pagination,
    offset: 0,
  });

  const results = data?.results ?? [];

  const onFetchMore = useCallback(() => {
    setPagination((prevState) => prevState + PAGINATE_BY);
  }, [setPagination]);

  const { handleSubmit, register, watch } = useForm<FormState>({
    defaultValues: {
      threadIds: [],
    },
    mode: 'onSubmit',
    shouldFocusError: false,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isUrl = isValidUrl(value);
    if (isUrl) {
      setSearch('');
      setRelatedThreadId(getThreadIdFromUrl(value));
      return;
    }
    setSearch(value.toLocaleLowerCase());
    setRelatedThreadId('');
  };

  const onSubmit = async (state: FormState) => {
    if (!user) return;
    const payloads: ICreateThreadMutation[] = state.threadIds.map((id) => ({
      relatedThread: id,
      creator: user?.id,
    }));

    try {
      await Promise.all(
        payloads.map((payload) => createRelatedThread({ payload, threadId }).unwrap())
      );
      showSuccessMessage(NOTIFICATION.RELATED_THREAD_CREATED);
      close();
    } catch (error) {
      showUncaughtErrorMessage();
    }
  };

  const selectedThreads = useMemo(
    () => results?.filter((thread) => watch('threadIds').includes(`${thread.id}`)),
    [results, watch('threadIds')]
  );

  const unselectedThreads = useMemo(
    () => results?.filter((thread) => !watch('threadIds').includes(`${thread.id}`)),
    [results, watch('threadIds')]
  );

  const stickySelectedThreadsHeader = useMemo(() => {
    const stickyClasses = 'left-0 z-[99] w-full px-6 pb-2';
    return (
      <div
        role="button"
        onClick={() => selectedThreadsListRef.current?.scroll({ top: 0 })}
        onKeyDown={() => null} // Required for accessibility/linting.
        tabIndex={0}
        className={`text-lg ${showStickySelectedThreadsInfo ? stickyClasses : ''}`}
        style={
          showStickySelectedThreadsInfo
            ? { position: 'fixed', background: colors.dark1, color: colors.gray2 }
            : { position: 'static', color: colors.gray2 }
        }
        data-cy="modal-add-related-thread-form-title"
      >
        Selected Threads ({selectedThreads.length})
      </div>
    );
  }, [selectedThreads, showStickySelectedThreadsInfo]);

  return (
    <Stack gap="24px" fullHeight data-cy="modal-add-related-thread-stack">
      <NewThreadButton
        open={open}
        name={name}
        tags={tags || []}
        threadId={threadId}
        isRelatedThread
      />
      <Divider color={colors.blue3} />
      <Title heading="h6" color={colors.gray2} cypressAttribute="modal-add-related-thread-title">
        Search Threads
      </Title>
      <Layout.SearchBar data-cy="modal-add-related-thread-search">
        <Layout.SearchIcon aria-hidden="true">
          <Icon fill={colors.gray1} icon="SearchIcon" size="small" />
        </Layout.SearchIcon>
        <TextInput
          autoFocus
          type="text"
          name="search-threads"
          placeholder="Search thread by name or paste url"
          onChange={handleSearchChange}
          style={{ paddingLeft: '44px' }}
          data-cy="modal-add-related-thread-search-input"
        />
      </Layout.SearchBar>
      <Divider color={colors.blue3} />
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          height: '100%',
          gap: '24px',
          display: 'flex',
          overflow: 'hidden',
          flexDirection: 'column',
        }}
        data-cy="modal-add-related-thread-form"
      >
        <Stack
          ref={selectedThreadsListRef}
          onScroll={(event) =>
            setShowStickySelectedThreadsInfo((event.target as HTMLElement).scrollTop > 50)
          }
          gap="24px"
          className="h-full pt-2"
          style={{ overflow: 'scroll' }}
        >
          {selectedThreads.length > 0 && (
            <>
              <Stack gap="12px" data-cy="modal-add-related-thread-stack">
                {stickySelectedThreadsHeader}
                {selectedThreads.map((thread) => (
                  <RelatedThread
                    key={thread.id}
                    {...register('threadIds')}
                    creator={thread.creator}
                    value={thread.id}
                    label={thread.name}
                  />
                ))}
              </Stack>
              <Divider color={colors.blue3} />
            </>
          )}

          <Stack gap="12px">
            {unselectedThreads.map((thread) => (
              <RelatedThread
                key={thread.id}
                {...register('threadIds')}
                creator={thread.creator}
                value={thread.id}
                label={thread.name}
              />
            ))}
          </Stack>
          {!!data?.count && results.length !== data?.count && (
            <Group fluid justify="center" margin="10px 0">
              <Button type="button" color={colors.dark2} variant="secondary" onClick={onFetchMore}>
                Load more
              </Button>
            </Group>
          )}
        </Stack>
        <Group justify="flex-end" gap="15px" style={{ marginTop: 'auto' }}>
          <Button
            variant="secondary"
            onClick={close}
            color={colors.dark2}
            data-cy="modal-add-related-thread-close-btn"
          >
            Close
          </Button>
          <Button
            type="submit"
            disabled={!watch('threadIds').length || isLoading}
            data-cy="modal-add-related-thread-save-btn"
          >
            Save
          </Button>
        </Group>
      </form>
    </Stack>
  );
};

export default AddRelatedThread;
